<script setup lang="ts">
import {useFormatting} from '@/composables/currency-format.ts';
import AegSessionStatusPopover from '@/app/admin/components/aeg-session-status-popover/aeg-session-status-popover.vue';
import {PaymentMethod, SessionDetailDto, SessionState, SessionType} from '@/api/enterprise-gateway-api.proxy.ts';
import {computed, type PropType} from 'vue';
import {paymentMethodLabels, sessionTypeLabels} from '@/app/admin/components/aeg-search-session/aeg-search-session.ts';
import {Button} from 'primevue';

const props = defineProps({
  sessionDetail: { type: Object as PropType<SessionDetailDto>, default: () => ({})},
});

const {formatCurrency, formatDateTime} = useFormatting();

const unknownLabel = 'onbekend';

const paymentMethodLabel = (paymentMethod: PaymentMethod | undefined) => {
  if (!paymentMethod) {
    return unknownLabel;
  }
  return paymentMethodLabels[paymentMethod] ?? unknownLabel;
}

const sessionTypeLabel = (sessionType: SessionType | undefined) => {
  if (!sessionType) {
    return unknownLabel;
  }
  return sessionTypeLabels[sessionType] ?? unknownLabel;
}

const isEditActorType = computed<boolean>(() => {
  return props.sessionDetail?.type === SessionType.EditActor;
});
const isPostPaidType = computed<boolean>(() => {
  return props.sessionDetail.type === SessionType.PostPaid;
});
const isSelfBillingType = computed<boolean>(() => {
  return props.sessionDetail.type === SessionType.SelfBilling;
});
const isSubmittedState = computed<boolean>(() => {
  return props.sessionDetail.state === SessionState.Submitted;
});
const showDocumentTransactionId = computed<boolean>(() => {
  return isPostPaidType.value || isSelfBillingType.value;
});
const isTopUpType = computed<boolean>(() => {
  return props.sessionDetail?.type === SessionType.TopUp;
});
const isPaymentType = computed<boolean>(() => {
  return props.sessionDetail?.type === SessionType.Payment;
});

</script>

<template>
  <div>
    <dl class="aeg-description-list">
      <dt class="aeg-description-list__term">Id:</dt>
      <dd class="aeg-description-list__description">{{ sessionDetail.sessionId }}</dd>
      <template v-if="!isEditActorType">
        <dt class="aeg-description-list__term">TransactionId:</dt>
        <dd class="aeg-description-list__description">{{ sessionDetail.transactionId }}</dd>
      </template>
      <template v-if="showDocumentTransactionId && sessionDetail.documentTransactionId">
        <dt class="aeg-description-list__term">DocumentTransactionId:</dt>
        <dd class="aeg-description-list__description">{{ sessionDetail.documentTransactionId }}</dd>
      </template>
      <template v-if="isPostPaidType && sessionDetail.invoiceSettingId">
        <dt class="aeg-description-list__term">InvoiceSettingId:</dt>
        <dd class="aeg-description-list__description">{{ sessionDetail.invoiceSettingId }}</dd>
      </template>
      <dt class="aeg-description-list__term">
        <div class="flex flex-row">Status: <aeg-session-status-popover/></div>
      </dt>
      <dd class="aeg-description-list__description">{{ sessionDetail.state }}</dd>
      <dt class="aeg-description-list__term">Aangemaakt:</dt>
      <dd class="aeg-description-list__description">
        {{ formatDateTime(sessionDetail.creationTimeUtc) }}
      </dd>
      <template v-if="sessionDetail.submittedTimeUtc">
        <dt class="aeg-description-list__term">Doorgestuurd:</dt>
        <dd class="aeg-description-list__description">
          {{ formatDateTime(sessionDetail.submittedTimeUtc) }}
        </dd>
      </template>
      <template v-if="sessionDetail.closedTimeUtc">
        <dt class="aeg-description-list__term">Afgesloten:</dt>
        <dd class="aeg-description-list__description">
          {{ formatDateTime(sessionDetail.closedTimeUtc) }}
        </dd>
      </template>

      <dt class="aeg-description-list__term">Type:</dt>
      <dd class="aeg-description-list__description">{{ sessionTypeLabel(sessionDetail.type) }}</dd>
      <template v-if="sessionDetail.paymentDetails">
        <dt class="aeg-description-list__term">Betaalmethode:</dt>
        <dd class="aeg-description-list__description">
          <template v-if="isSubmittedState">
            <Button link :href="sessionDetail.paymentDetails.paymentServiceProviderLink" target="_blank">
              {{ paymentMethodLabel(sessionDetail.paymentDetails.paymentMethod) }}
              <aeg-icon icon="pi pi-external-link" />
            </Button>
          </template>
          <template v-else>
            {{ paymentMethodLabel(sessionDetail.paymentDetails.paymentMethod) }}
          </template>
        </dd>
        <dt class="aeg-description-list__term">Ogm:</dt>
        <dd class="aeg-description-list__description">{{ sessionDetail.paymentDetails.paymentServiceProviderMerchantReference ?? '-' }}</dd>
      </template>
      <template v-if="sessionDetail.paymentDetails && isTopUpType">
        <dt class="aeg-description-list__term">Bedrag:</dt>
        <dd class="aeg-description-list__description">{{ formatCurrency(sessionDetail.paymentDetails.topUpAmount) }}</dd>
      </template>
      <dt class="aeg-description-list__term">Service:</dt>
      <dd class="aeg-description-list__description aeg-uppercase">{{ sessionDetail.service }}</dd>
      <template v-if="isPaymentType && sessionDetail.callbackUrl">
        <dt class="aeg-description-list__term">Service redirect url:</dt>
        <dd class="aeg-description-list__description">{{ sessionDetail.callbackUrl }}</dd>
      </template>
    </dl>
  </div>
</template>

<style scoped lang="scss">

</style>
